/*
 * Saga to set default channel as active on messages targetUser change
 *
 * Request patient information:
 * - family and provider circles,
 * - upcoming surveys,
 * - joined programs
 * - program invitations (disabled)
 */

import {
  fork,
  takeLatest,
  take,
  put,
  select,
  cancel,
} from 'redux-saga/effects';

import {
  defaultChannelIdSelector,
  availableTargetUserChannelsSelector,
  activeChannelIdSelector,
} from '../selectors';
import { defaultFirstActiveChannel } from '../../board/selectors/channels';
import chatConsts from '../constants';
import { consts as reqCircleOfCare } from '../../board/reqCircleOfCare';
import { consts as circleOfCare } from '../../board/circleOfCare';
import { consts as settings } from '../../board/settings';
import { consts as context } from '../../context';
import { consts as permissions } from '../../context/permissions';
import { consts as getUser } from '../../profiles/get-user';
import { isDesktop } from '../../../utils/mobileHelpers';
import { APPOINTMENT_CHANNEL_ID } from '../../../constants';

function* setActiveChannelWorker(action) {
  const { id } = action.payload;
  const state = yield select();
  const firstChannelId = yield select(defaultFirstActiveChannel);
  const activeChannelId = yield select(activeChannelIdSelector);
  const availableChannels = availableTargetUserChannelsSelector(state);
  const nextActiveTargetUserChannel = defaultChannelIdSelector(state);

  yield put({
    type: context.SET_TARGET_USER_ID,
    payload: id,
  });

  if (!isDesktop()) return;

  if (nextActiveTargetUserChannel === APPOINTMENT_CHANNEL_ID) {
    yield take(getUser.SUCCEED);

    yield put({
      type: chatConsts.SET_ACTIVE_CHANNEL_ID,
      payload: APPOINTMENT_CHANNEL_ID,
    });
    yield cancel();
  }

  yield put({ type: chatConsts.SET_ACTIVE_CHANNEL_ID });
  // yield delay(300); // uncomment if needed for smooth left sidebar animation

  // NOTE: if loggedUser is in the context of the patient
  // a) that is loggedUser itself or
  // b) is the one who gave caregiver permissions to the loggedUser -
  // we should check for upcoming surveys,
  // and if there are any - the default opened channel
  // should be the one with reminders

  // request patient data (to check permissions) and await for it to be parsed
  yield take(getUser.SUCCEED);
  yield take(permissions.UPDATE);

  if (!availableChannels) {
    yield put({
      type: chatConsts.SET_ACTIVE_CHANNEL_ID,
      payload: activeChannelId || firstChannelId,
    });
  } else {
    yield put({
      type: chatConsts.SET_ACTIVE_CHANNEL_ID,
      payload: parseInt(nextActiveTargetUserChannel, 10),
    });
  }
}

export function* setActiveChannelSaga() {
  yield takeLatest([settings.SET_LAST_USER_ID], setActiveChannelWorker);
}

function* storePatientCircleOfCareWorker(action) {
  const {
    familyCircle,
    careProviders,
    pendingInvitations = [],
    pendingCaregivers = [],
    pendingGuardiansProxies = [],
  } = action.payload;
  const pendingUsers = [
    ...pendingGuardiansProxies,
    ...pendingCaregivers,
    ...pendingInvitations,
  ];
  const pendingFamilyMembers = pendingUsers.map(item => {
    return {
      ...item,
      pending: true,
    };
  });

  yield put({
    type: circleOfCare.SET_CIRCLE,
    payload: {
      familyCircle,
      careProviders,
      pendingFamilyMembers,
    },
  });
}

export function* storePatientCircleOfCareSaga() {
  yield takeLatest([reqCircleOfCare.SUCCEED], storePatientCircleOfCareWorker);
}

function* clearCircleWorker() {
  yield put({
    type: circleOfCare.CLEAR,
  });
}

export function* clearCircleSaga() {
  yield takeLatest([reqCircleOfCare.SEND], clearCircleWorker);
}

export default function* root() {
  yield fork(setActiveChannelSaga);
  yield fork(storePatientCircleOfCareSaga);
  yield fork(clearCircleSaga);
}



// WEBPACK FOOTER //
// ./src/redux/chatConnection/sagas/setActiveChannel.js